'use client'

/**
 * Error Details Context
 *
 * Issue #1793 - Provides global state management for the error details modal
 * and integrates with the feedback modal for bug reporting.
 *
 * This context listens for custom events dispatched by toastError() and
 * manages the modal state and bug reporting flow.
 */

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type { ErrorDetails } from '@/lib/error-types'
import { ErrorDetailsModal } from '@/components/error-details-modal'
import { FeedbackModal } from '@/components/feedback-modal'
import { FeedbackType } from '@prisma/client-cadence'
import { useFailOpenContext } from '@/contexts/use-fail-open-context'
import { ERROR_IDS } from '@/lib/errors/constants'

type ErrorDetailsContextValue = {
  /** Show the error details modal with the given details */
  showErrorDetails: (details: ErrorDetails) => void
  /** Hide the error details modal */
  hideErrorDetails: () => void
}

const ErrorDetailsContext = createContext<ErrorDetailsContextValue | undefined>(
  undefined,
)

export { ErrorDetailsContext }

/**
 * Safe default for `useErrorDetails` when no provider is mounted
 * (#5860). The setters are no-ops; callers that try to surface an
 * error modal silently lose that one error rather than crashing the
 * page they're already on.
 */
const ERROR_DETAILS_FAIL_OPEN_DEFAULT: ErrorDetailsContextValue = {
  showErrorDetails: () => {},
  hideErrorDetails: () => {},
}

/**
 * Hook to access the error details context.
 *
 * Fail-open (#5860): if no `ErrorDetailsProvider` is mounted the
 * setters become no-ops and a one-shot structured log fires for
 * observability. Callers always receive callable setters.
 *
 * @example
 * const { showErrorDetails } = useErrorDetails()
 * showErrorDetails({
 *   message: 'Something went wrong',
 *   timestamp: new Date(),
 *   pageUrl: '/current-page',
 * })
 */
export function useErrorDetails() {
  return useFailOpenContext(ErrorDetailsContext, {
    hookName: 'useErrorDetails',
    errorId: ERROR_IDS.CONTEXT_ERROR_DETAILS_PROVIDER_MISSING,
    defaultValue: ERROR_DETAILS_FAIL_OPEN_DEFAULT,
  })
}

type ErrorDetailsProviderProps = {
  children: ReactNode
}

/**
 * Provider that manages the error details modal and feedback modal integration.
 *
 * This provider:
 * 1. Listens for 'open-error-details' custom events from toastError()
 * 2. Manages the ErrorDetailsModal visibility and state
 * 3. Handles the "Report Bug" flow to the FeedbackModal
 */
export function ErrorDetailsProvider({ children }: ErrorDetailsProviderProps) {
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState('')

  const showErrorDetails = useCallback((details: ErrorDetails) => {
    setErrorDetails(details)
    setIsErrorModalOpen(true)
  }, [])

  const hideErrorDetails = useCallback(() => {
    setIsErrorModalOpen(false)
    // Clear details after animation delay
    // If modal is reopened, new details will be set anyway
    setTimeout(() => {
      setErrorDetails(null)
    }, 200)
  }, [])

  // Listen for custom event from toastError()
  useEffect(() => {
    function handleOpenErrorDetails(event: CustomEvent<ErrorDetails>) {
      showErrorDetails(event.detail)
    }

    window.addEventListener(
      'open-error-details',
      handleOpenErrorDetails as EventListener,
    )
    return () => {
      window.removeEventListener(
        'open-error-details',
        handleOpenErrorDetails as EventListener,
      )
    }
  }, [showErrorDetails])

  /**
   * Handle the "Report Bug" button click from ErrorDetailsModal
   * Formats the error details and opens the FeedbackModal with pre-filled data
   */
  const handleReportBug = useCallback((details: ErrorDetails) => {
    // Format error details for bug report
    const lines = [
      `**Error:** ${details.message}`,
      '',
      `**Page:** ${details.pageUrl || '/'}`,
      `**Time:** ${details.timestamp.toLocaleString()}`,
    ]

    if (details.statusCode) {
      lines.push(`**Status Code:** ${details.statusCode}`)
    }

    if (details.requestUrl) {
      lines.push(`**Request URL:** ${details.requestUrl}`)
    }

    if (details.digest) {
      lines.push(`**Digest:** ${details.digest}`)
    }

    if (details.stack) {
      lines.push('', '**Stack Trace:**', '```', details.stack, '```')
    }

    if (details.context) {
      lines.push(
        '',
        '**Context:**',
        '```json',
        JSON.stringify(details.context, null, 2),
        '```',
      )
    }

    setFeedbackMessage(lines.join('\n'))
    setIsErrorModalOpen(false)
    setIsFeedbackModalOpen(true)
  }, [])

  /**
   * Handle closing the feedback modal
   * Clears state to prepare for next error
   */
  const handleCloseFeedback = useCallback(() => {
    setIsFeedbackModalOpen(false)
    setFeedbackMessage('')
    setErrorDetails(null)
  }, [])

  return (
    <ErrorDetailsContext value={{ showErrorDetails, hideErrorDetails }}>
      {children}
      <ErrorDetailsModal
        isOpen={isErrorModalOpen}
        onClose={hideErrorDetails}
        errorDetails={errorDetails}
        onReportBug={handleReportBug}
      />
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={handleCloseFeedback}
        initialType={FeedbackType.BUG}
        initialMessage={feedbackMessage}
      />
    </ErrorDetailsContext>
  )
}
